import { useState } from 'react';
import { decimalesDuPays } from '../lib/currency';
import { tr, trf } from '../lib/i18n';
import { C } from '../theme/palette';

/** Écrit un taux comme le pays l'écrit : « 5,3 » en Suisse romande. */
const ecrit = (n) => String(+(+n).toFixed(4)).replace('.', decimalesDuPays());

/**
 * Lit un taux tapé : « 5,3 », « 5.3 », « 5 % ». Rend `null` pour une case
 * vide ou un texte qui n'est pas un nombre.
 *
 * @param {string} texte ce qui est tapé
 * @returns {number|null} le taux, ou null
 */
function litTaux(texte) {
  const n = String(texte ?? '')
    .replace('%', '')
    .replace(',', '.')
    .trim();
  if (n === '' || !/^\d*\.?\d*$/.test(n) || n === '.') return null;
  return +n;
}

/**
 * Une case de taux qui se défend contre les erreurs de frappe.
 *
 * <p>Effacer la case pour retaper enregistrait 0 % sur-le-champ ; 150 % ou
 * un taux négatif passaient sans un mot ; et rien ne disait quel était le
 * taux normal. La case n'enregistre maintenant qu'un nombre entre 0 et
 * 100 ; une case laissée vide reprend son taux en quittant la case ; un taux
 * inhabituel est signalé ; le taux par défaut est écrit dessous, avec « ↺ »
 * pour y revenir d'un geste.
 *
 * @param {object} props propriétés
 * @param {string} props.libelle nom du taux
 * @param {number} props.valeur taux en vigueur
 * @param {number} [props.defaut] taux par défaut (barème), si connu
 * @param {number} [props.inhabituel] au-delà, le taux est signalé à vérifier
 * @param {(n: number) => void} props.onChange enregistre un taux valable
 * @param {() => void} [props.onRemettre] revient au taux par défaut
 * @param {boolean} [props.pourcent] « % » après le nom (non quand le titre de
 *   la section le dit déjà)
 * @returns {import('react').ReactElement} la case
 */
export function CaseTauxVerifiee({
  libelle,
  valeur,
  defaut,
  inhabituel,
  onChange,
  onRemettre,
  pourcent = true,
}) {
  const [texte, setTexte] = useState(null);
  const lu = texte == null ? +valeur || 0 : litTaux(texte);
  const impossible = texte != null && texte.trim() !== '' && (lu == null || lu > 100);
  const vide = texte != null && texte.trim() === '';
  const signale = !impossible && lu != null && inhabituel != null && lu > inhabituel;
  const different = defaut != null && Math.abs((+valeur || 0) - defaut) > 1e-9;
  const couleurBord = impossible ? C.red : signale ? C.amber : C.border;

  return (
    <div>
      <div style={{ fontSize: 8, fontWeight: 800, color: C.muted, marginBottom: 4 }}>
        {tr(libelle) + (pourcent ? ' %' : '')}
      </div>
      <input
        type="text"
        inputMode="decimal"
        aria-label={tr(libelle) + ' %'}
        aria-invalid={impossible || undefined}
        value={texte ?? ecrit(valeur || 0)}
        onChange={(e) => {
          const v = e.target.value;
          setTexte(v);
          const n = litTaux(v);
          if (n != null && n <= 100) onChange(n);
        }}
        onBlur={() => setTexte(null)}
        style={{
          width: '100%',
          border: '1.5px solid ' + couleurBord,
          borderRadius: 8,
          padding: '8px',
          fontSize: 12,
          boxSizing: 'border-box',
        }}
      />
      {impossible && (
        <div role="alert" style={{ fontSize: 8.5, color: C.red, fontWeight: 700, marginTop: 3 }}>
          {tr('Taux impossible : entre 0 et 100 %. Rien n’est enregistré.')}
        </div>
      )}
      {vide && (
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 3 }}>
          {trf('Case vide : le taux reste {0} %.', ecrit(valeur || 0))}
        </div>
      )}
      {signale && (
        <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: 3 }}>
          {tr('Taux inhabituel : vérifie-le.')}
        </div>
      )}
      {defaut != null && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 8.5,
            color: C.muted,
            marginTop: 3,
          }}
        >
          <span>{trf('par défaut {0} %', ecrit(defaut))}</span>
          {different && onRemettre && (
            <button
              type="button"
              onClick={() => {
                setTexte(null);
                onRemettre();
              }}
              aria-label={trf('Remettre {0} à {1} %', tr(libelle), ecrit(defaut))}
              style={{
                border: 'none',
                background: C.navyXL,
                color: C.navy,
                borderRadius: 6,
                padding: '2px 6px',
                fontSize: 8.5,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ↺ {ecrit(defaut)} %
            </button>
          )}
        </div>
      )}
    </div>
  );
}
